/**
 * The per-event extra fields — the one place they are defined.
 *
 * This lives in `shared/` rather than `functions/src/event/` because three places
 * need the same answer: the order form validates as you type, `createOrder`
 * validates for real, and the admin panel and e-mail label the values. Declaring
 * the fields twice is how a 16-character limit ends up being 20 in one of them.
 *
 * `functions/src/event/extraSchema.ts` re-exports this, so `core/` keeps importing
 * a fixed path and stays byte-identical to the template across events.
 *
 * AVP Run 2026: just a free-text note. The back-of-shirt nickname was removed once
 * the organiser confirmed nothing is printed per person.
 */
import { z } from 'zod'

export const NOTE_MAX = 300

export const EventExtraSchema = z
  .object({
    note: z.string().trim().max(NOTE_MAX, `หมายเหตุยาวไม่เกิน ${NOTE_MAX} ตัวอักษร`).optional(),
  })
  .strict('มีข้อมูลเพิ่มเติมที่ระบบไม่รู้จัก')

export type EventExtra = z.infer<typeof EventExtraSchema>

/** Labels for the summary card, the admin panel, the e-mail and the export. */
export const EVENT_EXTRA_LABELS: Record<keyof EventExtra, string> = {
  note: 'หมายเหตุ',
}

/**
 * Empty strings are dropped so an untouched optional field does not become an
 * `extra: { note: '' }` map on the order, a column in the factory export and a
 * blank row in the confirmation e-mail.
 */
export const parseEventExtra = (raw: unknown): EventExtra | undefined => {
  if (raw === undefined || raw === null) return undefined
  const parsed = EventExtraSchema.parse(raw)
  const kept = Object.entries(parsed).filter(([, value]) => typeof value === 'string' && value !== '')
  return kept.length > 0 ? (Object.fromEntries(kept) as EventExtra) : undefined
}

export const eventExtraRows = (extra: unknown): { label: string; value: string }[] =>
  Object.entries((extra ?? {}) as Record<string, unknown>).map(([key, value]) => ({
    label: EVENT_EXTRA_LABELS[key as keyof EventExtra] ?? key,
    value: String(value),
  }))
